import React, { useState, useRef, useEffect } from "react";
import "./Drawer.css";
import Button from "react-bootstrap/Button";
import styles from "./Drawer.module.scss";
import Kot from "../kot/Kot.jsx";
import Invoice from "../invoice/Invoice.jsx";
import handleErrorToast from "../../utils/handleErrorToasts.js";
import { MoveLeft } from 'lucide-react';
import printInvoices from "../../hooks/printInvoices.js";
import {
  ChevronDown,
  ChevronUp,
  ChefHat,
  ReceiptText,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  X
} from "lucide-react";
import Modal from "react-bootstrap/Modal";
import TableSelectionModal from "../commonComponents/TableSelectionModal.jsx";
import createOrder from "../../services/order/createOrder.js";
import updateOrder from "../../services/order/updateOrder.js";
import {toast} from "react-toastify"
import ORDER_TOASTS from "../../utils/constants/toastMesages/orderToasts.js";
/**
 * Displays an item card with name, description, price, and quantity control.
 *
 * @component
 * @param {Object} props - component props.
 * @param {Object} props.item - this is the item object.
 * @param {string} props.item.name - name of the item.
 * @param {string} props.item.description - description of the item.
 * @param {number} props.item.price - price of an item.
 * @param {number} quantity - the current quantity of the item.
 * @param {Function} updateQuantity - function to update the item's quantity.
 * @param {Function} handleShow - for the Invoice modal to popup
 * @returns {JSX.Element} returns a react component displaying item details.
 * @author - Susovan Mishra; susovanm@ovaku.in
 */
function Drawer({addedOrder, setAddedOrder, items, itemQuantities, clear, handleShow, addedItemIds, updateQuantity, handleKotPrint, kotGenerated, setKotGenerated, setCustDetails, custDetails, setInvoiceData, invoiceData, updateItemQuantity, invoiceShow, handleInvoicePrint, onPrintInvoiceClick, setInvoiceGenerated}) {
  //Getting order types, customer type, pay from the environment
  const orderType = JSON.parse(process.env.REACT_APP_ORDER_TYPES_JSON)
  const customerType = process.env.REACT_APP_DEFAULT_CUSTOMER_NAME
  const paymentType = JSON.parse(process.env.REACT_APP_PAYMENT_TYPES_JSON)
  const addCustomer = process.env.REACT_APP_ADD_CUSTOMER === 'true'
  //For tracking if kot already been generated or not 
  const [show, setShow] = useState(false);

  //Useref for referencing the part that is required to be printed
  const printRef = useRef();

  //Kot closing functions
  const handleKotClose = () => setShow(false);
  const handleKotShow = () => setShow(true);

  // State to manage if the drawer is open or closed
  const [isOpen, setIsOpen] = useState(window.innerWidth > 1024);

  // This effect hook handles window resize events.
  useEffect(() => {
    // Define a function to update the state based on the current window width.
    const handleResize = () => {
      setIsOpen(window.innerWidth > 1024);
    };

    // Add an event listener for the 'resize' event.
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); 

  // Saves all the invoice data
  const [tableModal, setTableModal]=useState(false)

  //For handling steps to enter next input after customer name
  const [custNameStep, setCustNameStep] = useState(1);

  const isMobile = () => {
    // Heuristic: touch + narrow viewport or userAgent match
    const mq = window.matchMedia && window.matchMedia("(max-width: 900px)").matches;
    const touch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const mobileUA = /android|iphone|ipad|ipod|iemobile|wpdesktop|mobile/i.test(ua);
    return (mq && touch) || mobileUA;
  };

  useEffect(() => {
  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') {
      // Handle Enter key based on current step
      switch(custNameStep) {
        case 1:
          if (!custDetails.name) {
            // Simulate skip button click
            setCustDetails({
              ...custDetails,
              name: customerType,
            });
            setCustNameStep(2);
          } else {
            // Simulate next button click
            setCustNameStep(2);
          }
          break;
        case 2:
          if (!custDetails.mobileNumber) {
            // Simulate skip button click
            setCustDetails({
              ...custDetails,
              phone: "NA",
              paymentType: paymentType.CASH_PAYMENT,
            });
            setCustNameStep(3);
          } else {
            // Simulate next button click
            setCustNameStep(3);
          }
          break;
        case 3:
          // Only trigger if order isn't already placed and there are items
          if (!orderPlaced && selectedItems.length > 0) {
            const orderPayload = {
              ...custDetails,
              items: selectedItems.map(item => ({
                productId: item._id,
                quantity: itemQuantities[item._id],
              })),
            };
            OrderAddingApi(orderPayload);
            // OrderEditingApi();
          }
          break;
        case 4:
          if (show) {
            const success = await printInvoices('kot-print-section');
            if (success) {
              setKotGenerated(true);
              handleShow();
              setShow(false);
            }
          }
          break;
        case 5:
          if (!show) {
            const success = await printInvoices('print-invoice-div');
            if (success) {
              setInvoiceGenerated(true);
            }
          }
          break;
        // case 6: // Generate Invoice
        //   if (orderPlaced) {
        //     handleShow(); // Open invoice modal
        //   }
        //   break;
        default:
          break;
      }
    }
  };

  // Add event listener when component mounts
  window.addEventListener('keydown', handleKeyDown);
  
  // Clean up event listener
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, [custNameStep, custDetails]);

  //State for tracking if order is been placed or not
  const [orderPlaced, setOrderPlaced] = useState(false);

  //Handling order type state change
  const handleOrderTypeChange = (e) => {
    setCustDetails({
      ...custDetails,
      orderType: e.target.value
    });
  };

  // Local state to track selected tables
  const [selectedTables, setSelectedTables] = useState([]);

  // Function to open or close drawer
  const handleDrawerToggle = () => {
    setIsOpen(!isOpen);
  };

  //Calculate totak price based on the item quantities
  const total = items.reduce((total, item) => {
    return total + (itemQuantities[item._id] || 0) * item.price;
  }, 0);

  //Get selected items displayed on the drwaer 
  //Can use it to show the numbers of selected items based on item id
  const selectedItems = [...addedItemIds].reverse().map((_id) => items.find((item) => item._id === _id)).filter((item) => item && itemQuantities[item._id] > 0);

 //Button handler for new order
  const newOrder=()=>{
    clear();
    setKotGenerated(false);
    setCustDetails(prev => ({
      orderType: prev.orderType
    }));
    setCustNameStep(1);
    setSelectedTables([]);
    setInvoiceData([])
  }
  
   //Api for saving customer order data to backend
  const OrderAddingApi = async (custDetails) => {
    try {
      const result = await createOrder(custDetails);
      // Ensure backend generated an invoice number; if not, block the flow
      if (!result?.data?.invoiceNumber) {
        toast.error("Please go online to place order");
        setOrderPlaced(false);
        return null;
      }
      toast.success(ORDER_TOASTS.ORDER_ADDED);
      setInvoiceData(result);
      setOrderPlaced(true);
      // Removed handleKotShow() to prevent modal from opening
      setCustNameStep(4);
      // Call edit immediately after add
      await updateOrder(result?.data?._id, {
        ...result?.data,
        status: "completed"
      });
    } catch (error) {
      setOrderPlaced(false);
      toast.error(ORDER_TOASTS.ORDER_NOT_ADDED);
      return null;
    }
  };

  return (
    <div
      // Dynamically changing the class based on `isopen` state
      className={`drawer shadow-lg position-fixed bottom-0 ${
        isOpen ? "open" : "closed"
      }`}
    >
      <div>
      </div>
      {/* Chevron button to open/close drawer */}
      <div className="arrow" onClick={handleDrawerToggle}>
        {isOpen ? (
          <ChevronDown size={45} color="gray" />
        ) : (
          <ChevronUp size={45} color="gray" />
        )}
      </div>

      {/* When the drawer is open, show full details */}
      {isOpen && (
        <>
        <div className="p-2">
          <div className={`${styles.cartContainer}`}>
          <div className="d-flex pb-3 justify-content-between align-items-center">
            <h3 style={{width:"50%"}}>Total: ₹{total}</h3>
            <div style={{opacity:addedOrder && "0.7",  pointerEvents: addedOrder ? "none" : "auto"}} className={styles.orderToggle} role="group" aria-label="Order type toggle">
              <input
                type="radio"
                className="btn-check"
                name="orderType"
                id="dineIn"
                autoComplete="off"
                value="dine-in"
                checked={custDetails.orderType === orderType.DINEIN }
                onChange={handleOrderTypeChange}
              />
              <label
                htmlFor="dineIn"
                className={`${styles.btnPill} ${custDetails.orderType === orderType.DINEIN ? styles.active : ""}`}
                onClick={() => {
                  setCustDetails({
                    ...custDetails,
                    orderType: orderType.DINEIN,
                    tableNumber: "1"
                  });
                  setSelectedTables([1]);
                }}
              >
                Dine-In
              </label>

              <input
                type="radio"
                className="btn-check"
                name="orderType"
                id="takeAway"
                autoComplete="off"
                value="take-away"
                checked={custDetails.orderType === orderType.TAKEAWAY}
                onChange={handleOrderTypeChange}
              />
              <label
                htmlFor="takeAway"
                className={`${styles.btnPill} ${custDetails.orderType === orderType.TAKEAWAY ? styles.active : ""}`}
              >
                Take-away
              </label>
            </div>
          </div>
          <div className="d-flex flex-row justify-content-between gap-2">
            {/* <div className="bill-btn col-6">
              <Button className="w-100" disabled={kotGenerated ? true : false || !selectedItems.length ? true : false || !orderPlaced? true : false} onClick={handleKotShow} variant="danger">
                <ChefHat size={20} />
                KOT
              </Button>
            </div> */}
            {/* Bill Button */}
            <div className="bill-btn col-12 generateInvoiceButton">
              {/* <Button
                className="mb-4 w-100 bill-btn"
                variant="primary"
                onClick={async () => {
                  const success = await printInvoices('print-invoice-div');
                  if (success) {
                    setInvoiceGenerated(true);
                  }
                }}
                disabled={!selectedItems.length || !orderPlaced ? true : false}
              >
                <ReceiptText size={20} /> Generate Invoice
              </Button> */}
            </div>
          </div>
          </div>
          {/* Item list with a clear button */}
          <div className="d-flex justify-content-between pb-3">
            <h4>Items ({selectedItems.length})</h4>
            <Button disabled={addedOrder} variant="outline-danger" onClick={clear}>
              <X/>Clear
            </Button>
          </div>
          {/* This div contains items details*/}
          <div 
          style={{height:"180px"}} 
          className={`${styles.itemList} items-list`}>
            {selectedItems.length > 0 ? (
              selectedItems.map((item) => (
                <div
                  key={item._id}
                  style={{ alignItems: "center", opacity: addedOrder && '0.7' }}
                  className={`d-flex justify-content-between ${styles.cartItems}`}
                >
                <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                    <img
                      src={item.img}
                      height={30}
                      width={35}
                      alt=""
                      className={`${styles.ItemImage} me-2`}
                    />
                    <p
                      className={`mb-0 text-truncate ${styles.ImageParagraph}`}
                    >
                      {item.name} (x{itemQuantities[item._id]})
                    </p>
                  </div>
                  <div
                    className={`${styles.cartItemContainer} d-flex`}
                  >
                    <button
                      className={styles.cartItemButtons}
                      disabled={addedOrder}
                      onClick={() => updateQuantity(item._id, -1)}
                    >
                      -
                    </button>
                    <p
                      className={`${styles.ItemTotalPrice} px-3`}
                    >
                      ₹{item.price * itemQuantities[item._id]}
                    </p>
                    <button
                      className={styles.cartItemButtons}
                      disabled={addedOrder}
                      onClick={() => updateQuantity(item._id, 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))
            ) 
            : 
            (
              // If the cart is empty , this will be shown
              <div className="h-100 d-flex flex-column justify-content-center align-items-center gap-2 pb-4">
                <div
                  className={`${styles.emptyCart}`}
                  style={{
                    padding: window.innerWidth < 1024 ? "15px" : "30px",
                  }}
                >
                  <ShoppingCart size={window.innerWidth < 1024 ? 20 : 50} />
                </div>
                <h5 style={{ opacity: "60%" }}>Your cart looks empty 🤔</h5>
              </div>
            )}
          </div>
          <div className="responsiveDrawer">
            <hr />
            {/* Subtotal section */}
            <div className="d-flex justify-content-between">
              <p>Sub Total</p>
              <p>₹{total}</p>
            </div>

            {/* Total amount */}
            <div className="d-flex justify-content-between">
              <h4 className="fw-bold">Total</h4>
              <h4>₹{total}</h4>
            </div>
          </div>
        </div>
        </>
      )}
      {/* If chevron down arrow is clicked only Total amount will be shown and div height wil decrese*/}
      {!isOpen && (
        <div className="p-2">
          <div className="d-flex pb-2 justify-content-between align-items-center">
            <h3>Total: ₹{total}</h3>
            <div style={{opacity: addedOrder && "0.7",pointerEvents: addedOrder ? "none" : "auto" }} className={styles.orderToggle} role="group" aria-label="Order type toggle">
              <input
                type="radio"
                className="btn-check"
                name="orderType"
                id="dineIn"
                autoComplete="off"
                value="dine-in"
                checked={custDetails.orderType === orderType.DINEIN}
                onChange={handleOrderTypeChange}
              />
              <label
                htmlFor="dineIn"
                className={`${styles.btnPill} ${custDetails.orderType === orderType.DINEIN ? styles.active : ""}`}
                onClick={() => {
                  setCustDetails({
                    ...custDetails,
                    orderType: orderType.DINEIN,
                    tableNumber: "1"
                  });
                  setSelectedTables([1]);
                }}
              >
                Dine-In
              </label>

              <input
                type="radio"
                className="btn-check"
                name="orderType"
                id="takeAway"
                autoComplete="off"
                value="take-away"
                checked={custDetails.orderType === orderType.TAKEAWAY}
                onChange={handleOrderTypeChange}
              />
              <label
                htmlFor="takeAway"
                className={`${styles.btnPill} ${custDetails.orderType === orderType.TAKEAWAY ? styles.active : ""}`}
              >
                Take-away
              </label>
            </div>
          </div>
        </div>
      )}
        {addCustomer && (
        <div
          style={{ display: custNameStep > 2 ? "none" : "flex" }}
          className="d-flex w-100 col-12 justify-content-between align-items-center gap-2 pb-3"
        >
          {custNameStep !== 3 && custNameStep !==4 && <Button
              variant="light"
              disabled={custNameStep === 1}
              onClick={() => setCustNameStep(1)}
              className={`${styles.btnStyles}`}
            >
              <ChevronLeft size={22} />
            </Button>}
          {custNameStep === 1 && (
            <input
              name="name"
              value={custDetails.name}
              onChange={(e) =>
                setCustDetails({
                  ...custDetails,
                  [e.target.name]: e.target.value,
                })
              }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault(); // Prevent form submission
                    }
                  }}
              className={`${styles.step1Style} rounded-pill flex-grow-1 px-3`}
              placeholder="Add customer name"
              type="text"
            />
          )}

          {custNameStep === 2 && (
            <input
              name="mobileNumber"
              value={custDetails.mobileNumber}
              onChange={(e) =>
                setCustDetails({
                  ...custDetails,
                  [e.target.name]: e.target.value,
                })
              }
               onKeyDown={(e) => {
                if (e.key === 'Enter') {
                e.preventDefault();
              }
              }}
              className={`${styles.step2Styles} rounded-pill flex-grow-1 px-3`}
              placeholder="Add customer number"
              type="text"
            />
          )}

          {custNameStep === 1 &&
            (!custDetails.name ? (
              <Button
                className={styles.btnStyles}
                onClick={() => {
                  setCustDetails({
                    ...custDetails,
                    name: customerType,
                  });
                  setCustNameStep(2);
                }}
              >
                <ChevronRight color="black"/>
              </Button>
            ) : (
              <Button
                className={styles.btnStyles}
                onClick={() => {
                  setCustNameStep(2);
                }}
              >
                <ChevronRight color="black"/>
              </Button>
            ))}

          {custNameStep === 2 && (
           <>
             {!custDetails.mobileNumber ? (
               <Button
                 className={styles.btnStyles}
                 onClick={() => {
                   setCustDetails({
                     ...custDetails,
                     phone: "NA",
                     paymentType: paymentType.CASH_PAYMENT,
                   });
                   setCustNameStep(3);
                 }}
               >
                 <ChevronRight color="black"/>
               </Button>
             ) : (
               <Button
                 className={styles.btnStyles}
                 onClick={() => {
                   setCustNameStep(3);
                 }}
               >
                <ChevronRight color="black"/>
               </Button>
             )}
            </>
          )}
        </div>
        )}

        {addCustomer && custNameStep === 3 && !orderPlaced && (
          <div className="d-flex gap-3 w-100 justify-content-end pb-3">
            <Button onClick={() => setCustNameStep(2)} className={`${styles.btnStyles}`}><ChevronLeft color="black"/></Button>
            <Button 
           disabled={!selectedItems.length || orderPlaced ? true : false}
           onClick={async (e) => {
            e.preventDefault();
            const orderPayload = {
              ...custDetails,
              items: selectedItems.map(item => ({
                 productId: item._id,
                 quantity: itemQuantities[item._id],
               })),
             };
             await OrderAddingApi(orderPayload);
             // Add invoice printing logic after order placement
             const success = await printInvoices('print-invoice-div');
             if (success) {
               setInvoiceGenerated(true);
             }
             // Always refresh page after order placement and printing attempt
             setTimeout(() => {
               window.location.reload();
             }, 1000);
                }}
              variant="success" 
              className="w-100">Place Order</Button> 
          </div>
        )}

        {!addCustomer && !orderPlaced && (
          <div className="d-flex gap-3 w-100 justify-content-end pb-3">
            <Button 
           disabled={!selectedItems.length || orderPlaced ? true : false}
           onClick={async (e) => {
            e.preventDefault();
            const orderPayload = {
              ...custDetails,
              name: customerType,
              phone: "NA",
              paymentType: paymentType.CASH_PAYMENT,
              items: selectedItems.map(item => ({
                 productId: item._id,
                 quantity: itemQuantities[item._id],
               })),
             };
             await OrderAddingApi(orderPayload);
             // Add invoice printing logic after order placement
             const success = await printInvoices('print-invoice-div');
             if (success) {
               setInvoiceGenerated(true);
             }
             // Always refresh page after order placement and printing attempt
             setTimeout(() => {
               window.location.reload();
             }, 1000);
               }}
              variant="success" 
              className="w-100">Place Order</Button> 
          </div>
        )}
        {addCustomer && custNameStep === 4 && !isOpen &&
            <Button 
              onClick={async () => {
                const success = await printInvoices('print-invoice-div');
                if (success) {
                  setInvoiceGenerated(true);
                }
              }} 
              className="w-100"
            >
              Generate Invoice
            </Button>
        }

        {/* {!addCustomer && orderPlaced && !isOpen &&
            <Button 
              onClick={async () => {
                const success = await printInvoices('print-invoice-div');
                if (success) {
                  setInvoiceGenerated(true);
                }
              }} 
              className="w-100"
            >
              Generate Invoice
            </Button>
        } */}
      <div className="w-100">
          {/* <Button disabled={!selectedItems.length ? true : false} onClick={() => {newOrder(); setOrderPlaced(false)}} className="w-100 mt-3">New Order</Button> */}
         </div>
          <Modal centered show={show} onHide={handleKotClose}>
            <Modal.Header closeButton>
              <Modal.Title>Generate Kot</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div
                id="kot-print-section"
                ref={printRef}
                style={{ display: "block" }}
              >
            <Kot
              items={items}
              addedItemIds={addedItemIds}
              itemQuantities={itemQuantities}
              custDetails={custDetails}
              invoiceData={invoiceData}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="warning" onClick={() => handleShow()}>
            Print Invoice
          </Button>
          <Button variant="primary" onClick={async (e) => {
            e.preventDefault(); 
            e.stopPropagation(); 
            const success = await printInvoices('kot-print-section');
            if(success)setKotGenerated(true); 
            handleShow()}}>
            Print Kot
          </Button>
        </Modal.Footer>
      </Modal>
      <TableSelectionModal   
        custDetails={custDetails}
        setCustDetails={setCustDetails}  
        show={tableModal} 
        onHide={() => setTableModal(false)} 
        selectedTables={selectedTables} 
        setSelectedTables={setSelectedTables}/>
      
      {/* Hidden Invoice component for printing */}
      <div style={{ display: 'none' }}>
        <div id="print-invoice-div">
          {invoiceData && (
            <Invoice
              items={items}
              addedItemIds={addedItemIds}
              itemQuantities={itemQuantities}
              custDetails={custDetails}
              invoiceData={invoiceData}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Drawer;
